/**
 * AmortizationScheduleModal
 *
 * Modal displaying the full amortization schedule grouped by year.
 * Shows monthly breakdown of principal, interest, taxes, and PMI.
 */

import { useState } from 'react';
import { X, ChevronDown, ChevronRight } from 'lucide-react';
import type { AmortizationSummary, AmortizationYear } from '@/types/mortgage';
import { formatPrice } from '@/utils';

interface AmortizationScheduleModalProps {
    isOpen: boolean;
    onClose: () => void;
    amortization: AmortizationSummary;
    loanTerm: number;
}

export function AmortizationScheduleModal({
    isOpen,
    onClose,
    amortization,
    loanTerm,
}: AmortizationScheduleModalProps) {
    if (!isOpen) return null;

    const totalMonths = loanTerm * 12;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="absolute inset-4 md:inset-10 bg-white rounded-lg shadow-xl flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                            Amortization Schedule
                        </h2>
                        <p className="text-sm text-gray-500">
                            {loanTerm} years ({totalMonths} months)
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-3 gap-4 px-6 py-4 bg-gray-50 border-b">
                    <SummaryCard
                        label="Total Payments"
                        value={amortization.totalPayments}
                    />
                    <SummaryCard
                        label="Total Interest"
                        value={amortization.totalInterest}
                    />
                    <SummaryCard
                        label="Principal"
                        value={amortization.totalPrincipal}
                    />
                </div>

                {/* Table Header */}
                <div className="grid grid-cols-6 gap-2 px-6 py-3 bg-gray-100 text-xs font-medium text-gray-500 uppercase">
                    <div>Month</div>
                    <div className="text-right">Principal</div>
                    <div className="text-right">Interest</div>
                    <div className="text-right">Taxes</div>
                    <div className="text-right">PMI</div>
                    <div className="text-right">Balance</div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto">
                    {amortization.years.map((yearData) => (
                        <YearSection key={yearData.year} yearData={yearData} />
                    ))}
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// Sub-components
// ============================================================================

interface SummaryCardProps {
    label: string;
    value: number;
}

function SummaryCard({ label, value }: SummaryCardProps) {
    return (
        <div>
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-lg font-semibold text-gray-900">{formatPrice(value)}</p>
        </div>
    );
}

interface YearSectionProps {
    yearData: AmortizationYear;
}

function YearSection({ yearData }: YearSectionProps) {
    const [isExpanded, setIsExpanded] = useState(false);

    const monthFormatter = new Intl.DateTimeFormat('en-US', { month: 'long' });

    return (
        <div className="border-b">
            {/* Year Header */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between px-6 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
                <div className="flex items-center gap-2">
                    {isExpanded ? (
                        <ChevronDown size={16} className="text-gray-400" />
                    ) : (
                        <ChevronRight size={16} className="text-gray-400" />
                    )}
                    <span className="font-medium text-gray-900">{yearData.year}</span>
                </div>
                <div className="flex items-center gap-6 text-sm">
                    <span className="text-gray-500">
                        Principal: <span className="text-gray-900">{formatPrice(yearData.yearlyPrincipal)}</span>
                    </span>
                    <span className="text-gray-500">
                        Interest: <span className="text-gray-900">{formatPrice(yearData.yearlyInterest)}</span>
                    </span>
                </div>
            </button>

            {/* Month Rows */}
            {isExpanded && (
                <div>
                    {yearData.rows.map((row) => (
                        <div
                            key={row.month}
                            className="grid grid-cols-6 gap-2 px-6 py-2 text-sm border-t border-gray-100 hover:bg-gray-50"
                        >
                            <div className="text-gray-600">
                                {monthFormatter.format(row.date)}
                            </div>
                            <div className="text-right text-gray-900">
                                {formatPrice(row.principal)}
                            </div>
                            <div className="text-right text-gray-900">
                                {formatPrice(row.interest)}
                            </div>
                            <div className="text-right text-gray-900">
                                {formatPrice(row.taxes)}
                            </div>
                            <div className="text-right text-gray-900">
                                {formatPrice(row.pmi)}
                            </div>
                            <div className="text-right text-gray-600 text-xs">
                                {formatPrice(row.balance)}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
